import { createSlice, isAnyOf, PayloadAction } from "@reduxjs/toolkit"
import { AviTracerApi } from "../avitracerApi"
import Flight, { FlightPostFlightData, FlightWb } from "../models/flight"
import { createAppAsyncThunk } from "./hooks"
import { RootState } from "./store"


interface FlightsState {
    allFlights: Flight[],
    status: "idle" | "success"
    loading: boolean
}

const initialState: FlightsState = {
    allFlights: [],
    status: "idle",
    loading: false
}

export const getFlights = createAppAsyncThunk(
    "flights/fetch",
    async () => {
        return AviTracerApi.getFlights()
    }
)

export const refreshFlights = createAppAsyncThunk(
    "flights/refresh",
    async () => {
        return AviTracerApi.getFlights()
    }
)

//Reloads one flight into the list, e.g. after its risk assessment / training record was filled or signed
export const refreshFlight = createAppAsyncThunk(
    "flights/refreshOne",
    async (flightId: string) => {
        return AviTracerApi.getFlightWithId(flightId)
    }
)

export const getFlightsInRange = createAppAsyncThunk(
    "flights/fetchRange",
    async (params: {from: Date,to: Date}) => {
        return AviTracerApi.getFlightsInRange(params.from, params.to)
    }
)

export const editFlight = createAppAsyncThunk(
    "flights/edit",
    async (flight: Flight, thunkApi) => {
        try{
            const retFlight = await AviTracerApi.editFlight(flight)
            return retFlight
        }catch(e){
            return thunkApi.rejectWithValue(e)
        }
    }
)

export const editFlightWbLoads = createAppAsyncThunk(
    "flights/editWb",
    async (data: {flight: Flight, wb: FlightWb}) => {
        return AviTracerApi.editFlightWbLoads(data.flight, data.wb)
    }
)

export const deleteFlight = createAppAsyncThunk(
    "flights/delete",
    async (flight: Flight) => {
        return AviTracerApi.deleteFlight(flight.id!)
    }
)

export const createFlight = createAppAsyncThunk(
    "flights/create",
    async (flight: Flight) => {
        return AviTracerApi.createFlight(flight)
    }
)

export const createReverseFlight = createAppAsyncThunk(
    "flights/createReverse",
    async (params: {flightId: string, departureDateTime: Date}) => {
        return AviTracerApi.createReverseFlight(params.flightId, params.departureDateTime)
    }
)

export const duplicateFlight = createAppAsyncThunk(
    "flights/duplicate",
    async (params: {flightId: string, departureDateTime: Date}) => {
        return AviTracerApi.duplicateFlight(params.flightId, params.departureDateTime)
    }
)

export const requestBriefingCreation = createAppAsyncThunk(
    "flights/requestBriefing",
    async (flight: Flight) => {
        if (flight.id === undefined){
            return Promise.reject("Flight must have an id")
        }

        return AviTracerApi.requestBriefing(flight.id)
    }
)

export const scheduleBriefingCreate = createAppAsyncThunk(
    "flights/scheduleBriefingCreate",
    async (params: {flightId: string, scheduleTime: Date}) => {
        return AviTracerApi.scheduleBriefingCreate(params.flightId, params.scheduleTime)
    }
)

export const cancelScheduleBriefingCreate = createAppAsyncThunk(
    "flights/cancelScheduleBriefingCreate",
    async (flightId: string) => {
        return AviTracerApi.cancelScheduleBriefingCreate(flightId)
    }
)

export const discardBriefing = createAppAsyncThunk(
    "flights/discardBriefing",
    async (flight: Flight) => {
        return AviTracerApi.discardBriefing(flight.id!)
    }
)

export const pilotAcceptBriefing = createAppAsyncThunk(
    "flights/pilotAcceptBriefing",
    async (params: {flightId: string, signatureBase64: string, postFlightData: FlightPostFlightData}) => {
        return AviTracerApi.pilotAcceptBriefing(params.flightId, params.signatureBase64, params.postFlightData)
    }
)

export const fileFpl = createAppAsyncThunk(
    "flights/fileFpl",
    async (flightId: string) => {
        return AviTracerApi.fileFpl(flightId)
    }
)

export const cancelFpl = createAppAsyncThunk(
    "flights/cancelFpl",
    async (flightId: string) => {
        return AviTracerApi.cancelFpl(flightId)
    }
)

export const delayFpl = createAppAsyncThunk(
    "flights/delayFpl",
    async (params: {flightId: string, newTime: Date}) => {
        return AviTracerApi.delayFpl(params.flightId, params.newTime)
    }
)

export const scheduleFileFpl = createAppAsyncThunk(
    "flights/scheduleFileFpl",
    async (params: {flightId: string, scheduleTime: Date}) => {
        return AviTracerApi.scheduleFileFpl(params.flightId, params.scheduleTime)
    }
)

export const cancelScheduleFileFpl = createAppAsyncThunk(
    "flights/scheduleCancelFileFpl",
    async (flightId: string) => {
        return AviTracerApi.cancelScheduleFileFpl(flightId)
    }
)

export const flightsSlice = createSlice({
    name: "flights",
    initialState,
    reducers:{
        resetFlightsStateForOrganizationChange: (state) => {
            state.allFlights = []
            state.status = "idle"
            state.loading = false
        },
        markFlightBriefingAsReady: (state, action: PayloadAction<Flight>) => {
            state.allFlights.find((f) => f.id === action.payload.id)!.briefing!.status = "ready"
        },
        markFlightBriefingAsInProgress: (state, action: PayloadAction<Flight>) => {
            state.allFlights.find((f) => f.id === action.payload.id)!.briefing!.status = "in-progress"
        },
        upsertFlight: (state, action: PayloadAction<Flight>) => {
            const index = state.allFlights.findIndex((f) => f.id === action.payload.id)
            if (index === -1){
                state.allFlights.push(action.payload)
            }else{
                state.allFlights[index] = action.payload
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getFlights.fulfilled, (state, action) => {
            state.allFlights = mergeArrays(state.allFlights, action.payload, "id")
            state.status = "success"
        })
        builder.addCase(refreshFlights.fulfilled, (state, action) => {
            state.allFlights = mergeArrays(state.allFlights, action.payload, "id")
        })
        builder.addCase(refreshFlight.fulfilled, (state, action) => {
            //Only replace a flight already in the list; an unloaded list fetches it with the rest
            const index = state.allFlights.findIndex((f) => f.id === action.payload.id)
            if (index !== -1) {
                state.allFlights[index] = action.payload
            }
        })
        builder.addCase(getFlightsInRange.fulfilled, (state, action) => {
            state.allFlights = mergeArrays(state.allFlights, action.payload, "id")
            state.status = "success"
        })
        builder.addCase(editFlight.rejected, (state, action) => {
            if ('data' in (action.payload as any)){
                const index = state.allFlights.findIndex((f) => f.id === action.meta.arg.id!)
                state.allFlights[index] = (action.payload as {data: Flight}).data
            }
        })
        builder.addCase(createFlight.fulfilled, (state, action) => {
            state.allFlights.push(action.payload)
        })
        builder.addCase(createReverseFlight.fulfilled, (state, action) => {
            state.allFlights.push(action.payload)
        })
        builder.addCase(duplicateFlight.fulfilled, (state, action) => {
            state.allFlights.push(action.payload)
        })
        builder.addCase(deleteFlight.fulfilled, (state, action) => {
            state.allFlights = state.allFlights.filter((f) => f.id !== action.payload.id)
        })
        builder.addMatcher(isAnyOf(
            scheduleBriefingCreate.fulfilled,
            cancelScheduleBriefingCreate.fulfilled,
        ), (state, action) => {
            const index = state.allFlights.findIndex((f) => f.id === action.payload.id)
            if (index !== -1){
                state.allFlights[index].briefing = action.payload.briefing
            }
        })
        builder.addMatcher(isAnyOf(
            scheduleFileFpl.fulfilled,
            cancelScheduleFileFpl.fulfilled,
        ), (state, action) => {
            const index = state.allFlights.findIndex((f) => f.id === action.payload.id)
            if (index !== -1){
                state.allFlights[index].fplFile = action.payload.fplFile
            }
        })
        builder.addMatcher(isAnyOf(
            editFlight.fulfilled,
            editFlightWbLoads.fulfilled,
            discardBriefing.fulfilled,
            fileFpl.fulfilled,
            cancelFpl.fulfilled,
            delayFpl.fulfilled,
            pilotAcceptBriefing.fulfilled,
            requestBriefingCreation.fulfilled,
        ), (state, action) => {
            const index = state.allFlights.findIndex((f) => f.id === action.payload.id)
            if (index === -1){
                state.allFlights.push(action.payload)
            }else{
                state.allFlights[index] = action.payload
            }
        })
        builder.addMatcher(isAnyOf(
            getFlights.pending,
            getFlightsInRange.pending,
        ), (state) => {
            state.loading = true
        })
        builder.addMatcher(isAnyOf(
            getFlights.rejected,
            getFlightsInRange.rejected,
            getFlights.fulfilled,
            getFlightsInRange.fulfilled,
        ), (state) => {
            state.loading = false
        })
    }
})

export const { resetFlightsStateForOrganizationChange, markFlightBriefingAsReady, markFlightBriefingAsInProgress, upsertFlight } = flightsSlice.actions

export const getFlightWithId = (state: RootState, flightId: string) => state.flights.allFlights.find((f) => f.id === flightId)

export default flightsSlice.reducer

export const mergeArrays = <E extends {}> (arr1: E[], arr2: E[], key: keyof E) => {
    let merged = arr1.map((item) => {
        return {
            ...item,
            ...arr2.find((i) => i[key] === item[key]),
        }
    })
    return merged.concat(arr2.filter((i2) => !merged.find((i1) => i1[key] === i2[key])))
}